import { PayloadAction } from '@reduxjs/toolkit';

import { PatientCareGoalDto, PatientCareGoalSaveRequest, PatientCareGoalsClient } from '@medone/medonehp-api-client';

import { handleError } from '../../../../shared/common/HandleErrors';
import { shouldSkipByPatientId } from '../../../../shared/common/helpers';
import { Axios } from '../../../../shared/common/http';
import { AppDispatch, AppThunk, RootState } from '../../../../shared/store';
import { CensusState } from './models';
import { censusSlice } from './slice';

// A save either succeeds (list refetched) or comes back with a readable message for the modal to show inline.
// A stale rowVersion arrives as a succeeded:false envelope, but the save gate's own rules are thrown: the
// server raises MedOneValidationException, which the API maps to a 400 ProblemDetails, so the catch has to
// read them out.
export type SaveCareGoalResult = { succeeded: boolean; message?: string };

// SCRUM-1117 - the stale-patient guard reads census.currentCensus, which only the chart maintains. Census-less
// routes (the Clinical Coordinator Medical Summary) can still have it pinned to a patient edited earlier in the
// shift, so their goals would be discarded as another patient's; they opt out. The ownership tag on the list is
// what keeps that safe: the panel renders rows only while they are tagged to the patient it is showing.
// SCRUM-1503 - `planned` picks the list: the planned goals entered through Add Order, or the SDOH goals.
type CareGoalGuardOptions = { bypassActivePatientGuard?: boolean; planned?: boolean };

const shouldDiscard = (state: RootState, patientId: number, options?: CareGoalGuardOptions) => !options?.bypassActivePatientGuard && shouldSkipByPatientId(state.census, patientId);

// The two lists share every thunk below; only the state they write and read differs.
const listActions = (planned?: boolean) =>
    planned
        ? { setGoals: censusSlice.actions.setPlannedCareGoals, setLoading: censusSlice.actions.setPlannedCareGoalsLoading }
        : { setGoals: censusSlice.actions.setPatientCareGoals, setLoading: censusSlice.actions.setPatientCareGoalsLoading };

const listOwner = (state: RootState, planned?: boolean) => (planned ? state.census.plannedCareGoalsPatientId : state.census.patientCareGoalsPatientId);

export const reducers = {
    setPatientCareGoalsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.patientCareGoalsLoading = action.payload;
    },

    // The list and the patient it belongs to are written together so they can never disagree: a caller cannot
    // store rows and forget to say whose they are.
    setPatientCareGoals: (state: CensusState, action: PayloadAction<{ patientId: number; goals: PatientCareGoalDto[] }>) => {
        state.patientCareGoals = action.payload.goals;
        state.patientCareGoalsPatientId = action.payload.patientId;
    },

    setPlannedCareGoalsLoading: (state: CensusState, action: PayloadAction<boolean>) => {
        state.plannedCareGoalsLoading = action.payload;
    },

    setPlannedCareGoals: (state: CensusState, action: PayloadAction<{ patientId: number; goals: PatientCareGoalDto[] }>) => {
        state.plannedCareGoals = action.payload.goals;
        state.plannedCareGoalsPatientId = action.payload.patientId;
    },
};

// SCRUM-1117 — care goals live in the census slice (not a panel-local hook) because the chart drawer and the
// Medical Summary form both render the panel, and two copies of the list could disagree after a write.
export function fetchPatientCareGoals(patientId: number, options?: CareGoalGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const planned = options?.planned === true;
        const { setGoals, setLoading } = listActions(planned);

        dispatch(setLoading(true));
        // SCRUM-1117 - both mount points read this one list, so ANOTHER patient's rows have to go before the new
        // fetch resolves; the loading mask is translucent and they would stay readable underneath it. Rows that
        // already belong to this patient stay put: every save and delete awaits a refetch, and blanking those
        // flashes "no care goals documented" on the chart, the same misread on the common path.
        if (listOwner(getState(), planned) !== patientId) {
            dispatch(setGoals({ patientId, goals: [] }));
        }

        const client = new PatientCareGoalsClient(null, Axios);

        try {
            // SCRUM-1503 - the filter is sent both ways rather than omitted for SDOH goals: what an unfiltered request
            // returns is a server default the Portal cannot see, and an earlier revision of the contract had it
            // returning both lists, which would put planned goals on the SDOH panel.
            const response = await client.getByPatient(patientId, planned);

            // Guard against race condition: discard if user navigated to a different patient.
            if (shouldDiscard(getState(), patientId, options)) {
                return;
            }

            if (response.result.succeeded) {
                dispatch(setGoals({ patientId, goals: response.result.entity ?? [] }));
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        } finally {
            // In a finally, not after the try: the race guard returns early, and a discarded fetch that never
            // cleared the flag would leave the table spinning for good.
            dispatch(setLoading(false));
        }
    };
}

// goalId null = create, otherwise update. Refetches on success so the server-resolved code system lands.
export function savePatientCareGoal(
    patientId: number,
    goalId: number | null,
    request: PatientCareGoalSaveRequest,
    options?: CareGoalGuardOptions
): AppThunk<Promise<SaveCareGoalResult>> {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientCareGoalsClient(null, Axios);

        try {
            const response = goalId != null ? await client.update(patientId, goalId, request) : await client.create(patientId, request);

            if (response.result.succeeded) {
                // Skip the refetch if the user has since navigated to another patient.
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientCareGoals(patientId, options));
                }

                return { succeeded: true };
            }

            return { succeeded: false, message: response.result.message };
        } catch (error) {
            // Only a 400 carries the save gate's own wording; a 500's detail is server internals, not a rule a
            // clinician can act on, so it stays behind the generic message.
            const validationDetail = error?.status === 400 ? error?.response?.detail : null;

            // The modal surfaces this inline; don't also toast via handleError, or a thrown save (e.g. a dropped
            // connection) would report the same failure twice.
            return { succeeded: false, message: validationDetail || `Something went wrong saving the ${options?.planned ? 'planned' : 'SDOH'} goal.` };
        }
    };
}

// Removal has no inline surface — the row's Popconfirm closes either way — so a failure toasts.
export function removePatientCareGoal(patientId: number, goalId: number, options?: CareGoalGuardOptions): AppThunk {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        const client = new PatientCareGoalsClient(null, Axios);

        try {
            const response = await client.deleteGoal(patientId, goalId);

            if (response.result.succeeded) {
                if (!shouldDiscard(getState(), patientId, options)) {
                    await dispatch(fetchPatientCareGoals(patientId, options));
                }
            } else {
                handleError(response, () => dispatch(censusSlice.actions.setError(null)));
            }
        } catch (error) {
            handleError(error, () => dispatch(censusSlice.actions.setError(error.toString())));
        }
    };
}

// One shared instance, never an inline []: a fresh array on every render would break the selector's referential
// check and re-render the panel on every unrelated census change.
const NO_GOALS: PatientCareGoalDto[] = [];

// SCRUM-1117 - the list and its ownership tag are read together, so there is no ungated way to render the rows.
// The fetch that replaces them is dispatched from an effect, which React commits AFTER the render, so reading
// the list alone paints the previous patient's coded concept and clinical text under the new patient's header.
export const selectPatientCareGoalsFor = (patientId?: number) => (state: RootState) =>
    state.census.patientCareGoalsPatientId === patientId ? state.census.patientCareGoals : NO_GOALS;

export const selectPatientCareGoalsLoading = (state: RootState) => state.census.patientCareGoalsLoading;

// SCRUM-1503 - the planned list, gated by its own ownership tag for the same reason as above.
export const selectPlannedCareGoalsFor = (patientId?: number) => (state: RootState) =>
    state.census.plannedCareGoalsPatientId === patientId ? state.census.plannedCareGoals : NO_GOALS;

export const selectPlannedCareGoalsLoading = (state: RootState) => state.census.plannedCareGoalsLoading;
